import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Grid, List as ListIcon, Map, ChevronLeft, ChevronRight, ChevronDown, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BeachCard from "@/components/BeachCard";
import BeachMap from "@/components/BeachMap";
import FilterChips from "@/components/FilterChips";
import SearchBar from "@/components/SearchBar";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { getBeaches, getStates } from "@/lib/api";

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [beaches, setBeaches] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedBeach, setSelectedBeach] = useState(null);

  // Filters
  const page = parseInt(searchParams.get("page") || "1");
  const search = searchParams.get("q") || "";
  const state = searchParams.get("state") || "";
  const sortBy = searchParams.get("sort") || "name";

  const [filters, setFilters] = useState({
    has_parking: searchParams.get("parking") === "true" || undefined,
    has_bathrooms: searchParams.get("bathrooms") === "true" || undefined,
    has_showers: searchParams.get("showers") === "true" || undefined,
    is_accessible: searchParams.get("accessible") === "true" || undefined,
    is_pet_friendly: searchParams.get("pets") === "true" || undefined,
  });

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const data = await getStates();
        setStates(data || []);
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    fetchStates();
  }, []);

  // Fetch beaches
  useEffect(() => {
    const fetchBeaches = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          per_page: 20,
          sort_by: sortBy,
        };
        if (search) params.search = search;
        if (state) params.state = state;
        Object.entries(filters).forEach(([key, value]) => {
          if (value === true) params[key] = true;
        });

        const data = await getBeaches(params);
        setBeaches(data.beaches || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 0);
      } catch (error) {
        console.error("Error fetching beaches:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBeaches();
  }, [page, search, state, sortBy, filters]);

  // Update URL
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === "" || value === false) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    if (!updates.page) newParams.set("page", "1");
    setSearchParams(newParams, { replace: true });
  };

  const handleSearch = (query) => {
    updateParams({ q: query || undefined, page: "1" });
  };

  const handleStateChange = (value) => {
    updateParams({ state: value === "all" ? undefined : value, page: "1" });
  };

  const handleSortChange = (value) => {
    updateParams({ sort: value, page: "1" });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    updateParams({
      parking: newFilters.has_parking ? "true" : undefined,
      bathrooms: newFilters.has_bathrooms ? "true" : undefined,
      showers: newFilters.has_showers ? "true" : undefined,
      accessible: newFilters.is_accessible ? "true" : undefined,
      pets: newFilters.is_pet_friendly ? "true" : undefined,
      page: "1",
    });
  };

  const handlePageChange = (newPage) => {
    updateParams({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectedStateName = states.find(s => s.slug === state)?.name || "";

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="search-page">
      <SeoHead
        title={selectedStateName ? `Public Beach Access in ${selectedStateName}` : "Search Public Beach Access Points"}
        description={selectedStateName
          ? `Explore ${total} public beach access points in ${selectedStateName}. Filter by free parking, restrooms, showers, wheelchair accessibility, and pet-friendly beaches. Get driving directions and reviews.`
          : `Search and filter ${total} public beach access points across ${states.length} US states. Find beaches with free parking, restrooms, showers, ADA accessibility, and more. Plan your perfect beach day.`}
        path={`/search${state ? `?state=${state}` : ''}`}
      />
      {/* Hero Header with State Selector */}
      <div className="bg-[#0EA5E9] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl font-extrabold font-['Outfit'] mb-2">
              🏖️ Find Beach Access Points
            </h1>
            <p className="text-white/80">
              {total.toLocaleString()} beach access points across {states.length} states
            </p>
          </div>

          {/* Prominent State Selector */}
          <div className="max-w-2xl mx-auto">
            <label className="block text-center text-white/90 font-medium mb-3">
              Select a State to Browse
            </label>
            <div className="bg-white rounded-2xl p-2 shadow-xl">
              <Select value={state || "all"} onValueChange={handleStateChange}>
                <SelectTrigger 
                  className="w-full h-14 text-lg font-semibold border-0 bg-transparent text-gray-800 focus:ring-0"
                  data-testid="state-selector-hero"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0EA5E9] rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <SelectValue placeholder="🇺🇸 All States" />
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  <SelectItem value="all" className="py-3">
                    <span className="font-semibold">🇺🇸 All States</span>
                  </SelectItem>
                  {states.map((s) => (
                    <SelectItem key={s.slug} value={s.slug} className="py-2">
                      <span className="font-medium">{s.name}</span>
                      <span className="text-gray-500 ml-2">({s.beach_count} beaches)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1 min-w-[200px]">
              <SearchBar
                initialValue={search}
                onSearch={handleSearch}
                placeholder="Search by name..."
                size="default"
              />
            </div>

            {/* Filter Chips */}
            <div className="flex-1 overflow-x-auto">
              <FilterChips filters={filters} onChange={handleFilterChange} />
            </div>

            {/* Sort & View Controls */}
            <div className="flex items-center gap-2 ml-auto">
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[140px] bg-gray-50" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name A-Z</SelectItem>
                  <SelectItem value="rating">Top Rated</SelectItem>
                  <SelectItem value="reviews">Most Reviews</SelectItem>
                </SelectContent>
              </Select>

              <div className="hidden sm:flex border border-gray-200 rounded-lg overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className={`rounded-none ${viewMode === "grid" ? "bg-[#0EA5E9] text-white" : ""}`}
                  data-testid="view-grid-btn"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className={`rounded-none ${viewMode === "list" ? "bg-[#0EA5E9] text-white" : ""}`}
                  data-testid="view-list-btn"
                >
                  <ListIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode("split")}
                  className={`rounded-none ${viewMode === "split" ? "bg-[#0EA5E9] text-white" : ""}`}
                  data-testid="view-split-btn"
                >
                  <Map className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Header */}
      {state && selectedStateName && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Link to="/search" className="hover:text-[#0EA5E9]">All States</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="font-medium text-gray-800">{selectedStateName}</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 font-['Outfit']">
            Beach Access in {selectedStateName}
          </h2>
        </div>
      )}

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Results count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-500 text-sm">
            {loading ? "Loading..." : `Showing ${beaches.length} of ${total.toLocaleString()} results`}
          </p>
        </div>

        {viewMode === "split" ? (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="h-36 bg-gray-100 animate-pulse rounded-2xl" />
                ))
              ) : beaches.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No beaches found. Try adjusting your filters.
                </div>
              ) : (
                beaches.map((beach) => (
                  <div
                    key={beach.id}
                    onClick={() => setSelectedBeach(beach)}
                    className={`cursor-pointer ${
                      selectedBeach?.id === beach.id ? "ring-2 ring-[#0EA5E9] rounded-2xl" : ""
                    }`}
                  >
                    <BeachCard beach={beach} />
                  </div>
                ))
              )}
            </div>
            <div className="hidden lg:block sticky top-40 h-[calc(100vh-200px)] rounded-xl overflow-hidden">
              <BeachMap
                beaches={beaches}
                selectedBeach={selectedBeach}
                onBeachSelect={setSelectedBeach}
              />
            </div>
          </div>
        ) : (
          <div
            className={`grid gap-4 ${
              viewMode === "grid"
                ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                : "grid-cols-1 max-w-2xl"
            }`}
            data-testid="beach-results"
          >
            {loading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className="h-36 bg-gray-100 animate-pulse rounded-2xl" />
              ))
            ) : beaches.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                No beaches found. Try adjusting your filters.
              </div>
            ) : (
              beaches.map((beach) => <BeachCard key={beach.id} beach={beach} />)
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
            <Button
              variant="outline"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="bg-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-gray-500 px-4">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="bg-white"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SearchPage;
